import Translation from 'components/Translation'
import { useEffect, useState } from 'react'
import { classNames } from 'utils/dom-helpers'
import { PaymentMethod } from 'types/payment'
import { OrderType } from 'types/order'
import { useTenantContext } from 'context/TenantContext'
import {
    default_primary_color,
    default_border_color,
} from '../../context/AuthContext'
import { useRouter } from 'next/router'
import { PAYMENT_POLICY_KEY } from 'pages/dashboard/settings/paymentPolicy'
import {
    SHOW_FREE_BOOKING,
    SHOW_MEMBERSHIPS,
} from 'components/Modals/CustomBookingPage/GeneralForm'
import PaymentPolicy from 'components/PaymentPolicy/PaymentPolicy'

const enum PAYMENT_POLICY_OPTIONS {
    ALL_OPTIONS = 'allOptions',
    OPTIONAL_PAYMENT = 'optionalPayment',
    NOT_ALLOWED = 'notAllowed',
    IN_PERSON = 'inPerson',
}

export type PaymentOptionProps = {
    active_button?: PaymentMethod
    order_type?: OrderType
    onToggle(o: PaymentMethod): void
    activeButton?: () => void
}
export default function PaymentOptions(p: PaymentOptionProps) {
    const {
        stripe_connected_account_id: tenant_stripe_connected_account_id,
        primaryColor,
        db_settings,
    } = useTenantContext()
    const router = useRouter()
    const [checkParam, setCheckParam] = useState<boolean>(false)
    const [defaultPaymentPolicy, setDetaultPaymentPolicy] = useState(
        PAYMENT_POLICY_OPTIONS.ALL_OPTIONS
    )
    const [chosen_payment_option, setPaymentOption] = useState<
        PaymentMethod | undefined
    >(p.active_button)
    const [displayFreeTrial, setDisplayFreeTrial] = useState(true)
    const [displayMemberships, setDisplayMemberships] = useState(true)

    useEffect(() => {
        handleDefaultPaymentPolicy()
        handleDefaultDisplayMembershipValue()
        handleDefaultFreeTrialValue()
    }, [db_settings])

    const handleDefaultPaymentPolicy = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === PAYMENT_POLICY_KEY
        )
        if (found) {
            setDetaultPaymentPolicy(found?.value)
        }
    }
    const handleDefaultFreeTrialValue = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === SHOW_FREE_BOOKING
        )

        if (found) {
            setDisplayFreeTrial(found?.value == 'true' ? true : false)
        }
    }

    const handleDefaultDisplayMembershipValue = () => {
        const found = db_settings?.content?.find(
            (item: any) => item.name === SHOW_MEMBERSHIPS
        )

        if (found) {
            setDisplayMemberships(found?.value == 'true' ? true : false)
        }
    }

    useEffect(() => {
        setCheckParam(
            router?.pathname.includes('/book/memberships') ? true : false
        )
    }, [])

    useEffect(() => {
        if (defaultPaymentPolicy === PAYMENT_POLICY_OPTIONS.IN_PERSON) {
            setPaymentOption(PaymentMethod.LATER)
            p.onToggle(PaymentMethod.LATER)
            p?.activeButton && p?.activeButton()
        }
    }, [defaultPaymentPolicy])

    return (
        <div id="payment_options" className="flex flex-col mb-4">
            <div>
                <h4 className="lg:text-xl text-black font-semibold mb-3">
                    <Translation content_key="booking.payment" />
                </h4>
            </div>
            <div className="pb-4 lg:pb-0">
                <div className="flex flex-col lg:flex-row gap-3">
                    {/* Use Membership */}
                    {(p.order_type &&
                        [
                            OrderType.MEMBERSHIP,
                            OrderType.MEMBERSHIP_RENEWAL,
                        ].indexOf(p.order_type) >= 0) ||
                    defaultPaymentPolicy ===
                        PAYMENT_POLICY_OPTIONS.IN_PERSON
                        ? ''
                        : displayMemberships && (
                              <button
                                  className="rounded-lg aot-btn border"
                                  style={{
                                      backgroundColor:
                                          chosen_payment_option ===
                                          PaymentMethod.MEMBER
                                              ? `${
                                                    primaryColor ||
                                                    default_primary_color
                                                }40`
                                              : `${default_border_color}40`,
                                      borderColor:
                                          chosen_payment_option ===
                                          PaymentMethod.MEMBER
                                              ? primaryColor ||
                                                default_primary_color
                                              : default_border_color,
                                  }}
                                  type="button"
                                  onClick={() => {
                                      setPaymentOption(PaymentMethod.MEMBER)
                                      p.onToggle(PaymentMethod.MEMBER)
                                      p?.activeButton && p?.activeButton()
                                  }}
                              >
                                  <i
                                      className="feather-users mr-2"
                                      style={{
                                          color: primaryColor
                                              ? primaryColor
                                              : default_primary_color,
                                      }}
                                  />
                                  <Translation content_key="booking.i_am_member" />
                              </button>
                          )}

                    {/* Use Coupon */}
                    {checkParam ||
                    defaultPaymentPolicy ===
                        PAYMENT_POLICY_OPTIONS.IN_PERSON
                        ? null
                        : displayFreeTrial && (
                              <button
                                  className="rounded-lg aot-btn border"
                                  style={{
                                      backgroundColor:
                                          chosen_payment_option ===
                                          PaymentMethod.COUPON
                                              ? `${
                                                    primaryColor ||
                                                    default_primary_color
                                                }40`
                                              : `${default_border_color}40`,
                                      borderColor:
                                          chosen_payment_option ===
                                          PaymentMethod.COUPON
                                              ? primaryColor ||
                                                default_primary_color
                                              : default_border_color,
                                  }}
                                  type="button"
                                  onClick={() => {
                                      setPaymentOption(PaymentMethod.COUPON)
                                      p.onToggle(PaymentMethod.COUPON)
                                      p?.activeButton && p?.activeButton()
                                  }}
                              >
                                  <i
                                      className="feather-award mr-2"
                                      style={{
                                          color:
                                              primaryColor ||
                                              default_primary_color,
                                      }}
                                  />
                                  <Translation content_key="booking.i_have_coupon" />
                              </button>
                          )}

                    {/* Use Card */}
                    {!tenant_stripe_connected_account_id ||
                    defaultPaymentPolicy ===
                        PAYMENT_POLICY_OPTIONS.NOT_ALLOWED ||
                    defaultPaymentPolicy ===
                        PAYMENT_POLICY_OPTIONS.IN_PERSON ? null : (
                        <button
                            className={classNames(
                                'rounded-lg aot-btn border'
                            )}
                            type="button"
                            style={{
                                backgroundColor:
                                    chosen_payment_option ===
                                    PaymentMethod.CARD
                                        ? `${
                                              primaryColor ||
                                              default_primary_color
                                          }40`
                                        : `${default_border_color}40`,
                                borderColor:
                                    chosen_payment_option ===
                                    PaymentMethod.CARD
                                        ? primaryColor ||
                                          default_primary_color
                                        : default_border_color,
                            }}
                            onClick={() => {
                                setPaymentOption(PaymentMethod.CARD)
                                p.onToggle(PaymentMethod.CARD)
                                p?.activeButton && p?.activeButton()
                            }}
                        >
                            <i
                                className="feather-credit-card mr-2"
                                style={{
                                    color:
                                        primaryColor ||
                                        default_primary_color,
                                }}
                            />
                            <Translation content_key="booking.add_payment_info" />
                        </button>
                    )}
                    {(defaultPaymentPolicy ===
                        PAYMENT_POLICY_OPTIONS.OPTIONAL_PAYMENT ||
                        defaultPaymentPolicy ===
                            PAYMENT_POLICY_OPTIONS.IN_PERSON) &&
                        !checkParam && (
                            <button
                                className="rounded-lg aot-btn border"
                                style={{
                                    backgroundColor:
                                        chosen_payment_option ===
                                        PaymentMethod.LATER
                                            ? `${
                                                  primaryColor ||
                                                  default_primary_color
                                              }40`
                                            : `${default_border_color}40`,
                                    borderColor:
                                        chosen_payment_option ===
                                        PaymentMethod.LATER
                                            ? primaryColor ||
                                              default_primary_color
                                            : default_border_color,
                                }}
                                type="button"
                                onClick={() => {
                                    setPaymentOption(PaymentMethod.LATER)
                                    p.onToggle(PaymentMethod.LATER)
                                    p?.activeButton && p?.activeButton()
                                }}
                            >
                                <i
                                    className="feather-dollar-sign mr-2"
                                    style={{
                                        color:
                                            primaryColor ||
                                            default_primary_color,
                                    }}
                                />
                                <Translation content_key="booking.book_now_pay_later" />
                            </button>
                        )}
                </div>
            </div>
        </div>
    )
}
